import type { ProviderFilters } from "@/features/providers/types/providers.types";

const hasMeaningfulString = (value: unknown): value is string =>
  typeof value === "string" &&
  value.trim() !== "" &&
  value.trim().toLowerCase() !== "null" &&
  value.trim().toLowerCase() !== "undefined";

const parseIntegerValues = (values: number[] | undefined): number[] =>
  Array.isArray(values)
    ? values.filter((value) => Number.isFinite(value))
    : [];

export const buildCompaniesQuery = (
  filters: Partial<ProviderFilters>,
): unknown[][] => {
  const query: unknown[][] = [["null", "deleted"]];

  if (hasMeaningfulString(filters.search)) {
    // ECOPD #80: a space-separated search ("John Doe") previously went to
    // the backend as a single %John Doe% LIKE clause, which requires an
    // exact substring match. Legacy RoR splits on whitespace and requires
    // every word to appear somewhere in the name/contact_name fields
    // (AND across tokens), so multi-word searches like "John Doe" match
    // "Doe, John ..." too.
    const rawTerm = filters.search.trim();
    const tokens = rawTerm.split(/\s+/).filter(Boolean);

    const buildOrForTerm = (term: string): unknown[] => {
      const like = `%${term}%`;
      return [
        "or",
        ["like", "city", like],
        ["like", "contact_email", like],
        ["like", "contact_name", like],
        ["like", "contact_phone1", like],
        ["like", "contact_phone2", like],
        ["like", "name", like],
        ["like", "street_address", like],
      ];
    };

    if (tokens.length > 1) {
      // Each token must match at least one searchable field (AND across tokens).
      tokens.forEach((token) => {
        query.push(buildOrForTerm(token));
      });
    } else {
      query.push(buildOrForTerm(rawTerm));
    }
  }

  if (hasMeaningfulString(filters.provider_type) && filters.provider_type !== "No Filters") {
    query.push(["equal", "provider_type", filters.provider_type]);
  }

  const serviceAreaIds = parseIntegerValues(filters.service_areas);
  if (serviceAreaIds.length > 0) {
    query.push(["in", "service_areas.id", serviceAreaIds]);
  }

  return query;
};
